import { BreakpointObserver } from '@angular/cdk/layout';
import { NgClass } from '@angular/common';
import {
	AfterViewChecked,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { HammerModule } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { delay, take, takeUntil } from 'rxjs/operators';
import { BreadcrumbComponent, BreadcrumbService } from 'xng-breadcrumb';

import { environment } from 'environments/environment';
import { FormControlPipe } from 'app/shared/pipes/form-control.pipe';

import { ComponentBase } from '../shared/component.base';
import { NavigationComponent } from '../shared/components/navigation/navigation.component';
import { CartSummaryModel } from '../shared/models/cart.models';
import { NavigationHeaderItem, NavigationItem, SearchType } from '../shared/models/common.models';
import { UserProfile } from '../shared/models/user-profile.models';
import { AuthService } from '../shared/services/auth.service';
import { CartService } from '../shared/services/cart.service';
import { CategoriesService } from '../shared/services/categories.service';
import { GtmEventsService } from '../shared/services/gtm-events.service';
import { SessionStorageStorageService } from '../shared/services/session-storage.service';
import { ViewListingsService } from '../shared/services/view-listings.service';
import { NavigationMenuComponent } from './navigation-menu/navigation-menu.component';

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgClass,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSidenavModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatInputModule,
        ReactiveFormsModule,
        FormsModule,
        MatCheckboxModule,
        MatRadioModule,
        MatSlideToggleModule,
        MatToolbarModule,
        MatListModule,
        MatBadgeModule,
        HammerModule,
        BreadcrumbComponent,
        NavigationComponent,
        NavigationMenuComponent,
        FormControlPipe,
    ]
})
export class LayoutComponent extends ComponentBase implements OnInit, AfterViewChecked, OnDestroy {
	@ViewChild(NavigationComponent) navigationComp: NavigationComponent;
	navItems: NavigationHeaderItem[];
	searchTypes: SearchType[];
	formGroup: UntypedFormGroup;
	showNav: boolean = false;
	mouseOnNav: boolean = false;
	showNavSubscription: Subscription;
	showNavStatusChanged: Subject<NavigationHeaderItem> = new Subject<NavigationHeaderItem>();
	currentNavItem: NavigationHeaderItem;
	user: UserProfile;
	routeStepCount: number = 0;
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	/**
	 * Constructor
	 */
	constructor(
		private authService: AuthService,
		private breakpointObserver: BreakpointObserver,
		public cartService: CartService,
		public categoriesService: CategoriesService,
		private toastr: ToastrService,
		private breadCrumbService: BreadcrumbService,
		private formBuilder: UntypedFormBuilder,
		private router: Router,
		private viewListingService: ViewListingsService,
		private sessionStorage: SessionStorageStorageService,
		private gtmEventsService: GtmEventsService,
		private elementRef: ElementRef,
		private renderer: Renderer2,
	) {
		super();

		if (document.referrer && document.referrer.includes(environment.winePortalUrl)) {
			if (this.authService.token != '') {
				localStorage.setItem('returnUrl', window.location.pathname);
				this.authService.loginIfRequired();
			} else {
				this.authService.login();
			}
		}

		breakpointObserver.observe(['(max-width: 62.5rem)']).subscribe((result) => {
			this.smallScreen = result.matches;
		});

		breakpointObserver.observe(['(max-width: 24.375rem)']).subscribe((result) => {
			this.extraSmallScreen = result.matches;
		});

		this.formGroup = this.formBuilder.group({
			type: ['wine', Validators.required],
			searchText: [],
		});

		this.navItems = [
			{
				name: 'Shop wine',
				showHoverable: false,
				linkPrefix: 'category/',
				links: [
					{
						id: 0,
						urlSlug: 'listings',
						name: 'All listings',
					},
				],
			},
			{
				name: 'Shop accessories',
				showHoverable: false,
				linkPrefix: '',
				links: [
					{
						id: 0,
						name: 'All accessories',
						urlSlug: 'accessories',
					},
				],
			},
			{
				name: 'Resources',
				showHoverable: false,
				linkPrefix: 'resources/',
				links: [
					{
						name: 'Education',
						urlSlug: 'education',
					},
					{
						name: 'Most Collected Wines of 2023',
						urlSlug: 'most-collected-wines-of-2023',
					},
					{
						name: 'Glossary',
						urlSlug: 'glossary',
					},
					{
						name: 'Provenance Program',
						urlSlug: 'provenance-program',
					},
					{
						name: 'FAQ',
						urlSlug: 'faq',
					},
				],
			},
			{
				name: 'About',
				showHoverable: false,
				linkPrefix: 'about/',
				links: [
					{
						name: 'About Wine Ark',
						urlSlug: '',
					},
					{
						name: 'Manage Cellarage Offering',
						urlSlug: 'manage-cellarage-offering',
					},
					{
						name: 'Terms & Conditions',
						urlSlug: 'terms-and-conditions',
					},
					{
						name: 'Why Wine Ark?',
						urlSlug: 'why-wine-ark',
					},
					{
						name: 'Testimonials',
						urlSlug: 'testimonials',
					},
					{
						name: 'Cellar Locations',
						urlSlug: 'cellar-locations',
					},
				],
			},
			{
				name: 'Contact',
				showHoverable: false,
				url: 'contact',
				links: [],
			},
		];

		this.searchTypes = [
			{
				label: 'All wine',
				value: 'wine',
			},
			{
				label: 'Accessories',
				value: 'accessories',
			},
		];

		this.user = this.authService.userData;

		this.showNavSubscription = this.showNavStatusChanged.pipe(delay(250)).subscribe((state) => {
			if (this.mouseOnNav) {
				this.currentNavItem = state;
			}
		});
	}

	get cartSummary(): CartSummaryModel {
		return this.cartService.cartSummary;
	}

	get timeRemaining(): string {
		return this.cartService.timeRemaining;
	}

	get timeUnderTenMins(): boolean {
		return this.cartService.timeUnderTenMin;
	}

	navigateToAccount(): void {
		window.location.href = environment.winePortalUrl + '/account';
	}

	navigateToCellar(): void {
		this.user == null
			? (window.location.href = environment.winePortalUrl + '/sign-up')
			: (window.location.href = environment.winePortalUrl + '/cellar');
	}

	hoveredOnNav(navItem: NavigationHeaderItem): void {
		this.showNav = true;
		this.mouseOnNav = true;
		this.showNavStatusChanged.next(navItem);
	}

	ngOnDestroy(): void {
		this.showNavSubscription.unsubscribe();
	}

	login(): void {
		this.authService.login();
	}

	logout(): void {
		this.cartService.clearLocalCart();
		this.sessionStorage.clear();
		this.authService.logout();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.breadCrumbService.breadcrumbs$.pipe().subscribe((test) => {
			this.routeStepCount = test.length;
		});

		this.cartService.getCartIdFromLocalStorage();
		this.categoriesService
			.getNavigationItemsV1()
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
				next: (results) => {
					this.navItems[0].links = this.navItems[0].links.concat(results as NavigationItem[]);
				},
				error: () => {
					this.toastr.error(
						'An error has occurred, please try again. If the issue persists, please contact Wine Ark.',
						'Error occurred',
					);
				},
			});
	}

	ngAfterViewChecked(): void {
		const header =
			this.smallScreen || this.extraSmallScreen
				? this.elementRef.nativeElement.querySelector('#smallHeaderBar')
				: this.elementRef.nativeElement.querySelector('#headerBar');

		if (header) {
			const newHeight = `${header.scrollHeight}px`;
			document.documentElement.style.setProperty('--header-height', newHeight);
		}
	}

	changedRoute(): void {
		this.cartService.getCartIdFromLocalStorage();
	}

	preformSearch() {
		// Check which type we need to be on
		if (this.formGroup.get('type').value === 'wine') {
			// Search wines
			this.router.navigateByUrl(`/listings?filterText=${this.formGroup.get('searchText').value}`);
		} else {
			// search accessories
			this.router.navigateByUrl(`/accessories?filterText=${this.formGroup.get('searchText').value}`);
		}
	}

	search(): void {
		this.gtmEventsService.searchEvent(this.formGroup.get('searchText').value);

		// Check for listingIds
		if (this.formGroup.get('searchText').value != '' && !isNaN(Number(this.formGroup.get('searchText').value))) {
			//Searching by an ID
			// Check it is a valid listingId first
			this.viewListingService
				.getListingStatusForSearchV1(Number(this.formGroup.get('searchText').value))
				.pipe(take(1))
				.subscribe({
					next: (result) => {
						if (result.isPrivate) {
							this.router.navigate(['/listing/', result.listingId, result.privateKey]);
						} else {
							this.router.navigate(['/listing/', result.listingId]);
						}
					},
					error: () => {
						// No listing with Id found, do a normal search
						this.preformSearch();
					},
				});
		} else {
			this.preformSearch();
		}
	}

	openNavigation(): void {
		this.navigationComp?.openDrawer();
		document.body.style.overflow = 'hidden';
	}

	closeNavigation(): void {
		if (this.navigationComp.panelOpenState) {
			this.navigationComp?.closeDrawer();
			document.body.style.overflow = '';
		}
	}

	navigationOpenStatus(): boolean {
		return this.navigationComp?.panelOpenState;
	}
}
